import { CheckOutlined } from '@ant-design/icons';
import { Alert, Col, Form, Row, Spin, Steps, Typography } from 'antd';
import React from 'react';
import { Link } from 'react-router';
import { ScopeButton } from './buttons';
import { AuthA11yToggle } from './AuthA11yToggle';
import { FormInput, FormPasswordInput } from './formInputs';
import { ThemedLogo } from './ThemedLogo';
import { StrongTitleSpan } from './formSpans';
import { FormWrapper } from './formWrappers';
import { useAuthA11yMode } from '../hooks/useAuthA11yMode';

export type ValidateUserFormLabels = {
  logoAriaLabel: string;
  almostThere: string;
  changePasswordTitle: string;
  fillInDetailsStep: string;
  emailVerifiedStep: string;
  setPasswordStep: string;
  createPassword: string;
  passwordDescription: string;
  password: string;
  repeatPassword: string;
  pleaseEnterPassword: string;
  pleaseConfirmPassword: string;
  passwordDoNotMatch: string;
  passwordMin12: string;
  submit: string;
  invalidActivationTitle: string;
  invalidActivationDescription: string;
  invalidResetTitle: string;
  invalidResetDescription: string;
  emailAddress: string;
  requiredField: string;
  invalidEmail: string;
  resendActivation: string;
  resendReset: string;
  accessibilityMode: string;
};

export const DEFAULT_VALIDATE_USER_FORM_LABELS: ValidateUserFormLabels = {
  logoAriaLabel: 'Home',
  almostThere: 'Almost there!',
  changePasswordTitle: 'Change your password',
  fillInDetailsStep: 'Fill in Details',
  emailVerifiedStep: 'Email Verified',
  setPasswordStep: 'Set Password',
  createPassword: 'Please enter a password for your account',
  passwordDescription:
    "Password needs to contain more than 12 characters including uppercase, lowercase, symbols and numbers. Common words, names and surnames are easy to guess, and it's recommended not to use them.",
  password: 'Password',
  repeatPassword: 'Repeat Password',
  pleaseEnterPassword: 'Please input your password!',
  pleaseConfirmPassword: 'Please confirm your password!',
  passwordDoNotMatch: 'The password that you entered do not match!',
  passwordMin12: 'Password must be at least 12 characters',
  submit: 'Submit',
  invalidActivationTitle: 'Invalid activation link!',
  invalidActivationDescription:
    'This seems to be an invalid or expired activation link. You can re-send the correct code by entering your email below.',
  invalidResetTitle: 'Invalid password reset link!',
  invalidResetDescription:
    'This seems to be an invalid or expired password reset link. You can re-send the correct link by entering your email below.',
  emailAddress: 'Email address',
  requiredField: 'This field is required',
  invalidEmail: 'Please enter a valid email',
  resendActivation: 'Re-send activation',
  resendReset: 'Re-send reset link',
  accessibilityMode: 'Accessibility mode',
};

export type ValidateUserFormProps = {
  loading: boolean;
  userExists: boolean | null;
  activate: (password: string) => void;
  resend: (email: string) => void;
  resetPassword?: boolean;
  logo: string;
  logoDark?: string;
  logoLinkTo: string;
  logoWidth?: string;
  labels?: Partial<ValidateUserFormLabels>;
};

export const ValidateUserForm: React.FC<ValidateUserFormProps> = ({
  loading,
  userExists,
  activate,
  resend,
  resetPassword,
  logo,
  logoDark,
  logoLinkTo,
  logoWidth,
  labels: labelsProp,
}) => {
  const labels = { ...DEFAULT_VALIDATE_USER_FORM_LABELS, ...labelsProp };
  const { a11yMode, handleA11yModeChange, authFormClassName } =
    useAuthA11yMode();

  const handleSubmit = (values: { password: string }) => {
    activate(values.password);
  };

  const handleResendSubmit = (values: { email: string }) => {
    resend(values.email);
  };

  return (
    <FormWrapper>
      <Spin spinning={loading} style={{ marginTop: '100px' }} size="large">
        <Row justify="center">
          <Link to={logoLinkTo} aria-label={labels.logoAriaLabel}>
            <ThemedLogo src={logo} srcDark={logoDark} alt="" width={logoWidth} />
          </Link>
        </Row>
        <AuthA11yToggle
          placement="header"
          checked={a11yMode}
          onChange={handleA11yModeChange}
          label={labels.accessibilityMode}
        />
        {userExists && (
          <Row justify="center">
            <Typography.Title
              level={3}
              style={{
                fontWeight: 400,
                fontSize: 24,
                lineHeight: '24px',
                marginTop: '40px',
                color: 'var(--color-text-base, #2e2e2e)',
              }}
            >
              {resetPassword
                ? labels.changePasswordTitle
                : labels.almostThere}
            </Typography.Title>
          </Row>
        )}
        {!resetPassword && userExists && (
          <Row justify="center">
            <Col span={8}>
              <Steps
                current={2}
                labelPlacement="vertical"
                items={[
                  { title: labels.fillInDetailsStep },
                  { title: labels.emailVerifiedStep },
                  {
                    title: labels.setPasswordStep,
                    icon: <CheckOutlined style={{ fontSize: 14 }} />,
                  },
                ]}
              />
            </Col>
          </Row>
        )}
        <Row justify="center" style={{ textAlign: 'center' }}>
          <Col span={9}>
            {userExists ? (
              <Row justify="center">
                <Col span={23} style={{ marginTop: '40px' }}>
                  <StrongTitleSpan>{labels.createPassword}</StrongTitleSpan>
                  <Typography.Text
                    style={{
                      fontWeight: 400,
                      fontSize: 12,
                      lineHeight: '18px',
                      color: 'var(--color-text-muted, #5a6a6a)',
                    }}
                  >
                    <br />
                    {labels.passwordDescription}
                  </Typography.Text>
                </Col>
                <Col span={16} style={{ marginTop: '50px' }}>
                  <Form
                    layout="vertical"
                    requiredMark={false}
                    className={authFormClassName}
                    onFinish={handleSubmit}
                    style={{ marginBottom: '30px' }}
                  >
                    <Row
                      justify="center"
                      style={{
                        display: 'flex',
                        flexFlow: 'row',
                        marginBottom: '40px',
                      }}
                    >
                      <Form.Item
                        name="password"
                        label={labels.password}
                        rules={[
                          {
                            required: true,
                            message: labels.pleaseEnterPassword,
                          },
                          {
                            min: 12,
                            message: labels.passwordMin12,
                          },
                        ]}
                      >
                        <FormPasswordInput
                          placeholder={labels.password}
                          autoComplete="new-password"
                        />
                      </Form.Item>
                      <Form.Item
                        name="confirm"
                        label={labels.repeatPassword}
                        dependencies={['password']}
                        rules={[
                          {
                            required: true,
                            message: labels.pleaseConfirmPassword,
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (
                                !value ||
                                getFieldValue('password') === value
                              ) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error(labels.passwordDoNotMatch),
                              );
                            },
                          }),
                        ]}
                      >
                        <FormPasswordInput
                          lmargin="30px"
                          placeholder={labels.repeatPassword}
                          autoComplete="new-password"
                        />
                      </Form.Item>
                    </Row>
                    <Col span={24}>
                      <Form.Item>
                        <ScopeButton type="primary" htmlType="submit">
                          {labels.submit}
                        </ScopeButton>
                      </Form.Item>
                    </Col>
                  </Form>
                </Col>
              </Row>
            ) : (
              <Row justify="center">
                <Col span={24}>
                  <Alert
                    type="warning"
                    message={
                      resetPassword
                        ? labels.invalidResetTitle
                        : labels.invalidActivationTitle
                    }
                    description={
                      resetPassword
                        ? labels.invalidResetDescription
                        : labels.invalidActivationDescription
                    }
                    style={{
                      marginTop: '20px',
                      backgroundColor: 'var(--color-surface-raised, #ffffff)',
                      padding: '15px',
                      border: '1px solid var(--color-border, #dbeae3)',
                      borderRadius: '15px',
                    }}
                  />
                </Col>
                <Col span={24} style={{ marginTop: '10px' }}>
                  <Form
                    layout="vertical"
                    requiredMark={false}
                    className={authFormClassName}
                    onFinish={handleResendSubmit}
                    style={{ marginBottom: '30px' }}
                  >
                    <Row justify="center">
                      <Col span={16}>
                        <Form.Item
                          name="email"
                          label={labels.emailAddress}
                          rules={[
                            {
                              required: true,
                              message: labels.requiredField,
                            },
                            {
                              type: 'email',
                              message: labels.invalidEmail,
                            },
                          ]}
                        >
                          <FormInput
                            placeholder={labels.emailAddress}
                            autoComplete="email"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row justify="center">
                      <Form.Item>
                        <ScopeButton type="primary" htmlType="submit">
                          {resetPassword
                            ? labels.resendReset
                            : labels.resendActivation}
                        </ScopeButton>
                      </Form.Item>
                    </Row>
                  </Form>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Spin>
    </FormWrapper>
  );
};
